import { useEffect, useRef } from 'react';
import { readPhxMode } from '@/shared/ui/theme';
import { PHOENIX_TO_MAGIC_ACTIONS } from './types';

/**
 * Dark mode kontrakt pro Magic iframy (viz [.docs/integration/legacy-komunikace.md](../../../../.docs/integration/legacy-komunikace.md)).
 *
 * Dva kanály:
 * - **Load-time**: URL param `darkMode=1` (sestavuje [magicUrl.ts](./magicUrl.ts)) — Magic se
 *   vyrenderuje rovnou v dark, bez probliknutí.
 * - **Runtime**: postMessage `desktop/dark-mode` při přepnutí — viz `useSyncLegacyDarkMode`.
 */

/**
 * Aktuální (persistovaná) volba dark módu Phoenixu. Zdroj pravdy je `localStorage` přes
 * [readPhxMode](../../ui/theme/phxTheme.ts) — spolehlivé i při buildu URL, kdy ještě nemusí
 * být atribut `data-phx-mode` na `<html>` ustálený (usePhxTheme ho aplikuje až v effectu).
 */
export function isLegacyDarkModeEnabled(): boolean {
  return readPhxMode() === 'dark';
}

/**
 * Pošle Magic iframu runtime přepnutí dark módu standardní request obálkou
 * (`{ action, requestId, data: { enabled } }`). Magic odpoví `{ response, requestId, data }` —
 * odpověď nečekáme (fire-and-forget; vizuální přepnutí nemá follow-up).
 */
export function sendDarkModeToFrame(frame: Window | null, enabled: boolean): void {
  if (!frame) return;
  frame.postMessage(
    {
      action: PHOENIX_TO_MAGIC_ACTIONS.darkMode,
      requestId: crypto.randomUUID(),
      data: { enabled },
    },
    '*'
  );
}

/**
 * Sleduje document-level atribut `data-phx-mode` a při každém runtime přepnutí dark/light
 * pošle `desktop/dark-mode` do iframu vráceného `getFrame`.
 *
 * Proč MutationObserver a ne `usePhxTheme`: `usePhxTheme` drží lokální React state per
 * instance — toggle v `ModeSwitcheru` nepropíše stav do jiných komponent. Atribut na
 * `<html>` je naopak jediný sdílený zdroj pravdy, který se mění při každém přepnutí.
 *
 * Initial mount nevysílá: `last` se inicializuje z persistované volby, takže když se
 * boot atribut ustálí na uložené hodnotě, žádná falešná zpráva se nepošle — počáteční
 * stav už pokrývá URL param `darkMode=1` bez probliknutí.
 */
export function useSyncLegacyDarkMode(getFrame: () => Window | null): void {
  // Drží aktuální getter bez nutnosti re-subscribovat observer při změně identity callbacku.
  const getFrameRef = useRef(getFrame);
  getFrameRef.current = getFrame;

  useEffect(() => {
    const root = document.documentElement;
    let last = isLegacyDarkModeEnabled();
    const observer = new MutationObserver(() => {
      const next = root.getAttribute('data-phx-mode') === 'dark';
      if (next === last) return;
      last = next;
      sendDarkModeToFrame(getFrameRef.current(), next);
    });
    observer.observe(root, { attributes: true, attributeFilter: ['data-phx-mode'] });
    return () => observer.disconnect();
  }, []);
}
